import { useState } from 'react';
import {
    getLatestPricePerformancePoint,
    type PricePerformancePoint,
    type PricePerformanceResponse,
} from '../api/chartDataTypes';
import { formatReportDate, reportTypeLabel } from '../utils/perf10kFormat';
import { buildPricePerformanceSummary } from '../utils/pricePerformanceFormat';
import { ChartDataSectionShell } from './ChartDataSectionShell';
import { DataPointSelector } from './DataPointSelector';

type PricePerformanceChartPanelProps = {
    response: PricePerformanceResponse;
};

/**
 * Price-Performance orchestrator for STARS_ENRT securities. Mirrors
 * ShoutTnaChartPanel — the response has no pre-baked summary block, so the
 * default summary is built from the latest entry in `data.company_price` and
 * the user's date pick overrides it. STARS rank, target price, and index
 * pct_change snap to the nearest entry at-or-before the chosen date (see
 * `buildPricePerformanceSummary`).
 */
export function PricePerformanceChartPanel({ response }: PricePerformanceChartPanelProps) {
    const { security, title, report_date, data } = response;
    const latestPoint = getLatestPricePerformancePoint(response);

    const [selectedPoint, setSelectedPoint] = useState<PricePerformancePoint | null>(null);

    const headerTitle = `${security.name} (${security.ticker}) — ${title}`;
    const headerSubtitle = `${reportTypeLabel(security.report_type)} · Report date: ${formatReportDate(report_date)}`;

    const pointForSummary = selectedPoint ?? latestPoint;
    const summaryParts = pointForSummary ? buildPricePerformanceSummary(response, pointForSummary) : null;
    const summary = summaryParts?.text ?? 'No data available.';
    const summaryHTML = summaryParts?.html;

    return (
        <ChartDataSectionShell
            title={headerTitle}
            subtitle={headerSubtitle}
            summary={summary}
            summaryHTML={summaryHTML}>
            {data.company_price.length > 0 ? (
                <DataPointSelector<PricePerformancePoint>
                    points={data.company_price}
                    idPrefix='price_performance'
                    onSelectedPointChange={setSelectedPoint}
                />
            ) : null}
        </ChartDataSectionShell>
    );
}
